import { ref, computed, onMounted, onUnmounted } from 'vue';
import { supabase, CALL_RECORDS_TABLE } from 'dashboard/config/supabaseClient';

export function useSupabaseDashboard() {
  const callHistory = ref([]);
  const isLoading = ref(false);
  const error = ref(null);

  let realtimeSubscription = null;

  const callsStats = computed(() => {
    const answered = callHistory.value.filter(c => c.status === 'answered').length;
    const missed = callHistory.value.filter(c => c.status === 'missed').length;
    const received = answered + missed;

    const answeredCalls = callHistory.value.filter(c => c.status === 'answered' && c.duration > 0);
    const avgDuration = answeredCalls.length > 0
      ? Math.round(answeredCalls.reduce((sum, c) => sum + c.duration, 0) / answeredCalls.length)
      : 0;

    return { answered, missed, received, avgDuration, inQueue: 0 };
  });

  const callsByUnit = computed(() => {
    const unitMap = new Map();
    callHistory.value.forEach(call => {
      const unit = call.unit || 'Telefone';
      if (!unitMap.has(unit)) {
        unitMap.set(unit, { unit, answered: 0, missed: 0, total: 0 });
      }
      const stats = unitMap.get(unit);
      stats.total += 1;
      if (call.status === 'answered') stats.answered += 1;
      else if (call.status === 'missed') stats.missed += 1;
    });
    return Array.from(unitMap.values()).sort((a, b) => b.total - a.total);
  });

  const mapRecord = (record) => ({
    id: record.unique_id || record.id.toString(),
    callerNumber: record.caller_number,
    callerName: record.caller_name || 'Desconhecido',
    unit: record.unit || 'Telefone',
    duration: record.duration || 0,
    status: record.status,
    agentId: record.agent_id || null,
    timestamp: record.call_start || record.created_at,
    recordingUrl: record.recording_url,
  });

  const fetchCallHistory = async (options = {}) => {
    const { dateFrom = null, dateTo = null } = options;

    isLoading.value = true;
    error.value = null;

    try {
      let query = supabase
        .from(CALL_RECORDS_TABLE)
        .select('*')
        .order('call_start', { ascending: false })
        .limit(1000);

      if (dateFrom) query = query.gte('call_start', dateFrom);
      if (dateTo) query = query.lte('call_start', dateTo);

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      callHistory.value = (data || []).map(mapRecord);
    } catch (err) {
      error.value = err.message;
    } finally {
      isLoading.value = false;
    }
  };

  const updateCallInHistory = (record) => {
    const mapped = mapRecord(record);
    const existingIndex = callHistory.value.findIndex(c => c.id === mapped.id);
    if (existingIndex !== -1) {
      callHistory.value[existingIndex] = mapped;
    } else {
      callHistory.value.unshift(mapped);
    }
  };

  const subscribeToRealtime = () => {
    realtimeSubscription = supabase
      .channel('call_records:all')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: CALL_RECORDS_TABLE },
        (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            updateCallInHistory(payload.new);
          } else if (payload.eventType === 'DELETE') {
            const deletedId = payload.old.unique_id || payload.old.id.toString();
            callHistory.value = callHistory.value.filter(c => c.id !== deletedId);
          }
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          // eslint-disable-next-line no-console
          console.log('[Supabase Realtime] Conectado — escutando', CALL_RECORDS_TABLE);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // eslint-disable-next-line no-console
          console.error('[Supabase Realtime] Falha na subscription:', status, err);
        } else if (status === 'CLOSED') {
          // eslint-disable-next-line no-console
          console.warn('[Supabase Realtime] Canal fechado');
        }
      });
  };

  const unsubscribeFromRealtime = () => {
    if (realtimeSubscription) {
      supabase.removeChannel(realtimeSubscription);
      realtimeSubscription = null;
    }
  };

  const refresh = async (options = {}) => {
    await fetchCallHistory(options);
  };

  onMounted(async () => {
    // Padrão: hoje — calculado pelo Index.vue e passado via refresh()
    subscribeToRealtime();
  });

  onUnmounted(() => {
    unsubscribeFromRealtime();
  });

  return {
    callHistory: computed(() => callHistory.value),
    callsStats,
    callsByUnit: computed(() => callsByUnit.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    fetchCallHistory,
    refresh,
    subscribeToRealtime,
    unsubscribeFromRealtime,
  };
}
